// Sind 的資料模型：一張心智圖是一棵主題樹，加上跨分支的關聯線。

export const STRUCTURES = ['balanced', 'logic-right', 'logic-left', 'org'] as const;
export type Structure = (typeof STRUCTURES)[number];

export interface Topic {
  id: string;
  title: string;
  children: Topic[];
  icon?: string;
  color?: string;
  collapsed?: boolean;
  /** 外框分組標籤；空字串代表有外框但無標籤。外框包住此主題與其所有子孫。 */
  boundary?: string;
  note?: string;
  /** id 是否由作者明確指定（序列化時只輸出明確指定的 id）。 */
  explicitId?: boolean;
  /** 設計導圖用（大綱語法不讀寫）：節點旁的進度標籤 0～100 */
  progress?: number;
  /** 設計導圖用：標題以紅色顯示（模組有衝突標記） */
  alert?: boolean;
  /** 設計導圖用：收合的子孫中有衝突，節點右上角顯示紅點 */
  alertHidden?: boolean;
  /** 設計導圖用：淡化顯示（擱置的模組） */
  muted?: boolean;
}

export interface Relation {
  from: string;
  to: string;
  label?: string;
  /** 線型，預設虛線；設計導圖的依賴關係用實線 */
  style?: 'solid' | 'dashed';
}

export interface MindMap {
  root: Topic;
  relations: Relation[];
  structure: Structure;
  theme: string;
}

export interface Diagnostic {
  line: number;
  severity: 'error' | 'warning';
  message: string;
}

// 走訪整棵樹，回呼帶入主題、深度與父主題。
export function walk(topic: Topic, visit: (topic: Topic, depth: number, parent?: Topic) => void, depth = 0, parent?: Topic): void {
  visit(topic, depth, parent);
  for (const child of topic.children) walk(child, visit, depth + 1, topic);
}

// 依 id 建立索引，供關聯線與 MCP 操作查找。
export function indexTopics(root: Topic): Map<string, Topic> {
  const index = new Map<string, Topic>();
  walk(root, topic => index.set(topic.id, topic));
  return index;
}

// FNV-1a 32 位元雜湊；用於產生穩定 id 與筆觸亂數種子。
export function hash32(text: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}
